import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DayPilot, DayPilotScheduler } from '@daypilot/daypilot-lite-react';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json';

import {
  VIEWS,
  SPECIAL_ROWS,
  COLOR_OPTIONS,
} from './constants';
import {
  getStartForView,
  getDaysForView,
  buildFormFields,
} from './utils';
import { decoupledSave, fetchCollection } from '../../services/api';
import { assignmentsProcessor } from '../../dataProcessors/decoupledApiProcessors';
import { useModalManager } from './SchedulerFormModal';
import { SchedulerToolbar } from './SchedulerToolbar';
import { useSchedulerCrud } from './useSchedulerCrud';
import { useSchedulerRenderers } from './useSchedulerRenderers';

const ASSIGNMENTS_COLLECTION_URL = '/decoupled-api/node/assignment/collection?display=decoupled';

export const ResourceScheduler = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const attributes = useEvaluatedAttributes(props.attributes);

  /* ── Evaluated props from Reactive-JSON context ── */
  const rawEvents = evaluateTemplateValue({ valueToEvaluate: props.events, globalDataContext, templateContext }) || [];
  const rawResources = evaluateTemplateValue({ valueToEvaluate: props.resources, globalDataContext, templateContext }) || [];
  const publicHolidays = evaluateTemplateValue({ valueToEvaluate: props.publicHolidays, globalDataContext, templateContext }) || [];
  const customTaskTypes = evaluateTemplateValue({ valueToEvaluate: props.customTaskTypes, globalDataContext, templateContext }) || [];
  const projects = evaluateTemplateValue({ valueToEvaluate: props.projects, globalDataContext, templateContext }) || [];
  const tasks = evaluateTemplateValue({ valueToEvaluate: props.tasks, globalDataContext, templateContext }) || [];
  const isAdmin = evaluateTemplateValue({ valueToEvaluate: props.isAdmin, globalDataContext, templateContext }) ?? true;
  const currentUserId = evaluateTemplateValue({ valueToEvaluate: props.currentUserId, globalDataContext, templateContext }) ?? null;
  const capacityMode = evaluateTemplateValue({ valueToEvaluate: props.capacityMode, globalDataContext, templateContext }) ?? 'soft';
  const overlapMode = evaluateTemplateValue({ valueToEvaluate: props.overlapMode, globalDataContext, templateContext }) ?? 'permissive';
  const holidayMode = evaluateTemplateValue({ valueToEvaluate: props.holidayMode, globalDataContext, templateContext }) ?? 'soft';

  /* ── Local state ── */
  const [schedulerRef, setSchedulerRef] = useState(null);
  const [currentView, setCurrentView] = useState('month');
  const [startDate, setStartDate] = useState(getStartForView('month', DayPilot.Date.today()));
  const [events, setEvents] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterProject, setFilterProject] = useState('');
  const [filterType, setFilterType] = useState('');

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [containerWidth, setContainerWidth] = useState(0);
  const [customDays, setCustomDays] = useState(null);

  const toastTimer = useRef(null);
  const containerRef = useRef(null);
  const lastClickedEvent = useRef(null);

  /* ── Sync external events ── */
  useEffect(() => {
    if (Array.isArray(rawEvents)) setEvents(rawEvents);
  }, [rawEvents]);

  /* ── ResizeObserver (debounced) ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let timer;
    const ro = new ResizeObserver((entries) => {
      clearTimeout(timer);
      const w = Math.floor(entries[0].contentRect.width);
      timer = setTimeout(() => setContainerWidth(w), 150);
    });
    ro.observe(el);
    return () => { clearTimeout(timer); ro.disconnect(); };
  }, []);

  /* ── Derived view data ── */
  const allDays = useMemo(() => getDaysForView(currentView, startDate), [currentView, startDate]);
  const days = customDays ?? allDays;
  const viewConfig = VIEWS[currentView];
  const responsiveRowHeaderWidth = containerWidth > 0 && containerWidth < 640 ? 130 : 200;

  const responsiveCellWidth = useMemo(() => {
    if (!containerWidth || containerWidth <= 0) return viewConfig.cellWidth;
    const available = containerWidth - responsiveRowHeaderWidth - 2;
    return Math.max(Math.floor(available / days), customDays ? 20 : (currentView === 'week' ? 50 : 28));
  }, [containerWidth, days, customDays, currentView, responsiveRowHeaderWidth, viewConfig.cellWidth]);

  /* ── Select options for forms ── */
  const ctOptions = useMemo(
    () => customTaskTypes.map((ct) => ({ name: ct.name || ct.label, id: String(ct.id || ct.value) })),
    [customTaskTypes],
  );
  const prjOptions = useMemo(
    () => projects.map((p) => ({ name: p.name, id: String(p.id) })),
    [projects],
  );
  const taskOptions = useMemo(
    () => tasks.map((t) => {
      const d = t.data || t;
      return { name: d.title || d.name || `Tâche ${d.id}`, id: String(d.id), projectId: d.projectId ? String(d.projectId) : '' };
    }),
    [tasks],
  );
  const EXCLUDED_ROLES = ['client', 'observer'];
  const resources = useMemo(() => {
    let mapped = rawResources
      .filter((r) => {
        const roles = (r.roles || []).map((role) => (typeof role === 'string' ? role : role.id || '').toLowerCase());
        return !roles.some((role) => EXCLUDED_ROLES.includes(role));
      })
      .map((r) => ({
        id: String(r.id),
        name: r.name || `User ${r.id}`,
        dailyCapacity: r.dailyCapacity ?? 7,
      }));
    if (currentUserId && !isAdmin) {
      mapped = mapped.filter((r) => String(r.id) === String(currentUserId));
    }
    return mapped;
  }, [rawResources, currentUserId, isAdmin]);
  const resOptions = useMemo(
    () => resources.map((r) => ({ name: r.name, id: String(r.id) })),
    [resources],
  );

  /* ── Filtered events ── */
  const filteredEvents = useMemo(() => {
    let evs = events;
    if (currentUserId && !isAdmin) evs = evs.filter((e) => String(e.resource) === String(currentUserId));
    if (filterProject) evs = evs.filter((e) => String(e._meta?.projectId || '') === filterProject);
    if (filterType) evs = evs.filter((e) => String(e._meta?.customTaskType || '') === filterType);
    return evs;
  }, [events, currentUserId, isAdmin, filterProject, filterType]);

  /* ── Histogram resources (always visible) ── */
  const ROW_MIN_H = 93;
  const schedulerResources = useMemo(() => [
    { name: 'Charge', id: '_histogram', frozen: 'top', cellsAutoUpdated: true, cellsDisabled: true },
    { name: 'Taux', id: '_utilization', frozen: 'top', cellsAutoUpdated: true, cellsDisabled: true },
    ...resources.map((r) => ({ ...r, minHeight: ROW_MIN_H })),
  ], [resources]);

  /* ── Period statistics (scoped to visible date range) ── */
  const periodStats = useMemo(() => {
    const viewStart = new DayPilot.Date(startDate).getTime();
    const viewEnd = new DayPilot.Date(startDate).addDays(days).getTime();
    const visible = filteredEvents.filter((ev) => {
      const s = new DayPilot.Date(ev.start).getTime();
      const e = new DayPilot.Date(ev.end).getTime();
      return s < viewEnd && e > viewStart;
    });
    const total = visible.length;
    const resCount = resources.length;
    const assigned = new Set(visible.map((e) => String(e.resource))).size;
    let totalHours = 0;
    for (const ev of visible) {
      const s = Math.max(new DayPilot.Date(ev.start).getTime(), viewStart);
      const e = Math.min(new DayPilot.Date(ev.end).getTime(), viewEnd);
      const wPeriod = ev._meta?.workload;
      const workloadFactor = (Number.isFinite(wPeriod) ? wPeriod : 100) / 100;
      totalHours += ((e - s) / 3600000) * workloadFactor;
    }
    const avgPerRes = resCount > 0 ? Math.round(totalHours / resCount) : 0;
    return { total, assigned, resCount, avgPerRes };
  }, [filteredEvents, resources, startDate, days]);

  /* ── Static DayPilot objects ── */
  const separators = useMemo(() => [
    { color: '#f43f5e', location: DayPilot.Date.today(), width: 2, layer: 'AboveEvents' },
  ], []);
  const bubble = useMemo(() => {
    try { return new DayPilot.Bubble(); } catch { return undefined; }
  }, []);

  /* ── Filter options (derived from events) ── */
  const projectFilterOptions = useMemo(() => {
    const seen = new Set();
    return events
      .filter((e) => e._meta?.projectId && !seen.has(String(e._meta.projectId)) && seen.add(String(e._meta.projectId)))
      .map((e) => {
        const prj = projects.find((p) => p.id === e._meta.projectId);
        return { id: String(e._meta.projectId), name: prj?.name || `Projet ${e._meta.projectId}` };
      });
  }, [events, projects]);

  const typeFilterOptions = useMemo(() => {
    const seen = new Set();
    return events
      .filter((e) => e._meta?.customTaskType && !seen.has(String(e._meta.customTaskType)) && seen.add(String(e._meta.customTaskType)))
      .map((e) => {
        const ct = customTaskTypes.find((t) => String(t.id || t.value) === String(e._meta.customTaskType));
        return { id: String(e._meta.customTaskType), name: ct?.name || ct?.label || e._meta.customTaskType };
      });
  }, [events, customTaskTypes]);

  /* ── Toast ── */
  const showToast = useCallback((msg, type = 'warning') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  /* ── Modal manager (renders via Portal, stays in the React tree) ── */
  const { showFormModal, showConfirmModal, modalPortal } = useModalManager();

  /* ── Refresh assignments: refetch → dataProcessor → updateGlobalData ── */
  const refreshAssignments = useCallback(async () => {
    try {
      const raw = await fetchCollection(ASSIGNMENTS_COLLECTION_URL, true);
      const processed = assignmentsProcessor({
        requestContext: { url: ASSIGNMENTS_COLLECTION_URL },
        responseContext: { status: 200 },
        dataToProcess: raw,
      });
      if (globalDataContext.updateData) {
        globalDataContext.updateData(processed, 'assignments');
      }
    } catch (err) {
      console.warn('Post-save refresh failed (data was saved):', err);
    }
  }, [globalDataContext]);

  /* ── Per-resource utilization for the visible period ── */
  const resourceUtilization = useMemo(() => {
    const viewStart = new DayPilot.Date(startDate);
    const viewEndDate = viewStart.addDays(days);
    const viewStartMs = viewStart.getTime();
    const viewEndMs = viewEndDate.getTime();
    const startDow = viewStart.getDayOfWeek();
    let businessDays = 0;
    for (let i = 0; i < days; i++) {
      const dow = (startDow + i) % 7;
      if (dow !== 0 && dow !== 6) businessDays++;
    }
    if (businessDays === 0) return {};

    const util = {};
    for (const res of resources) {
      let weightedDays = 0;
      for (const ev of events) {
        if (String(ev.resource) !== String(res.id)) continue;
        const evStartMs = new DayPilot.Date(ev.start).getTime();
        const evEndMs = new DayPilot.Date(ev.end).getTime();
        if (evStartMs >= viewEndMs || evEndMs <= viewStartMs) continue;
        const oStart = new DayPilot.Date(Math.max(evStartMs, viewStartMs));
        const oEnd = new DayPilot.Date(Math.min(evEndMs, viewEndMs));
        const wUtil = ev._meta?.workload;
        const wFactor = (Number.isFinite(wUtil) ? wUtil : 100) / 100;
        let d = oStart;
        while (d.getTime() < oEnd.getTime()) {
          const dow = d.getDayOfWeek();
          if (dow !== 0 && dow !== 6) weightedDays += wFactor;
          d = d.addDays(1);
        }
      }
      util[String(res.id)] = Math.round((weightedDays / businessDays) * 100);
    }
    return util;
  }, [resources, events, startDate, days]);

  /* ── CRUD hook ── */
  const { editEvent, handleDelete, duplicateEvent, changeColor, validateAction, callbacksRef } = useSchedulerCrud({
    events, setLoading, showToast, refreshAssignments,
    prjOptions, ctOptions, resOptions, taskOptions, customTaskTypes,
    publicHolidays, resources, isAdmin,
    capacityMode, overlapMode, holidayMode,
    showFormModal, showConfirmModal,
  });

  /* ── Renderer hook (bug fix: currentView now in deps) ── */
  const {
    onBeforeEventRender,
    onBeforeCellRender,
    onBeforeTimeHeaderRender,
    onBeforeRowHeaderRender,
  } = useSchedulerRenderers({ projects, tasks, currentView, publicHolidays, isAdmin, resources, resourceUtilization });

  /* ── Context menu ── */
  const eventMenu = useMemo(() => {
    const dot = (hex) =>
      `<span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${hex};margin-right:8px;vertical-align:middle;box-shadow:0 0 0 1px ${hex}66"></span>`;
    try {
      return new DayPilot.Menu({
        items: [
          { text: '\u270F\uFE0E Éditer', onClick: (args) => callbacksRef.current.editEvent(args.source.data) },
          { text: '\u2398 Dupliquer', onClick: (args) => callbacksRef.current.duplicateEvent(args.source.data) },
          { text: '\u2716 Supprimer', onClick: (args) => callbacksRef.current.handleDelete(args.source.data) },
          { text: '-' },
          ...COLOR_OPTIONS.map((c) => ({
            html: dot(c.id) + c.name,
            onClick: (args) => callbacksRef.current.changeColor(args.source.data, c.id),
          })),
        ],
      });
    } catch { return undefined; }
  }, [callbacksRef]);

  /* ── Event click / double-click ── */
  const onEventClick = useCallback((args) => { lastClickedEvent.current = args.e.data; }, []);
  const onEventDoubleClick = useCallback((args) => { callbacksRef.current.editEvent(args.e.data); }, [callbacksRef]);

  /* ── Create event on time range selection ── */
  const onTimeRangeSelected = useCallback(async (args) => {
    schedulerRef?.clearSelection();
    if (SPECIAL_ROWS.includes(args.resource)) return;

    const form = buildFormFields(prjOptions, ctOptions, resOptions, taskOptions, true);
    const data = {
      text: 'Nouvelle affectation',
      start: args.start, end: args.end,
      resource: String(args.resource),
      id: DayPilot.guid(),
      backColor: '#6fa8dc', projectId: '', taskId: '', customTaskType: '', notes: '',
      workload: 100,
    };

    const modal = await showFormModal(form, data);
    if (modal.canceled) return;

    const result = modal.result;
    const wl = Number.isFinite(parseInt(result.workload, 10)) ? parseInt(result.workload, 10) : 100;
    const validation = validateAction(result.resource, result.start, result.end, wl);
    if (validation.blocked) { showToast(validation.message, 'error'); return; }
    if (validation.message) showToast(validation.message, 'warning');

    const ct = result.customTaskType ? customTaskTypes.find((t) => String(t.id || t.value) === result.customTaskType) : null;
    const color = ct?.color || result.backColor || '#6fa8dc';

    try {
      setLoading(true);
      await decoupledSave('node', 'assignment', {
        title: result.text,
        start: new DayPilot.Date(result.start).toString('yyyy-MM-ddTHH:mm:ss'),
        end: new DayPilot.Date(result.end).toString('yyyy-MM-ddTHH:mm:ss'),
        resource: result.resource, color,
        projectId: result.projectId || undefined,
        taskId: result.taskId || undefined,
        customTaskType: result.customTaskType || undefined,
        notes: result.notes || undefined,
        workload: wl,
      });
      await refreshAssignments();
    } catch (err) {
      showToast(`Erreur création : ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [schedulerRef, prjOptions, ctOptions, resOptions, taskOptions, customTaskTypes, validateAction, showToast, showFormModal, refreshAssignments]);

  /* ── Move event ── */
  const onEventMoved = useCallback(async (args) => {
    if (SPECIAL_ROWS.includes(String(args.newResource))) {
      showToast('Impossible de déplacer vers cette ligne', 'error');
      args.preventDefault();
      return;
    }
    if (!isAdmin && String(args.e.data.resource) !== String(args.newResource)) {
      showToast('Déplacement inter-ressource réservé aux administrateurs', 'error');
      args.preventDefault();
      return;
    }
    const evWorkload = args.e.data._meta?.workload ?? 100;
    const validation = validateAction(args.newResource, args.newStart, args.newEnd, evWorkload, args.e.data.id);
    if (validation.blocked) { showToast(validation.message, 'error'); args.preventDefault(); return; }
    if (validation.message) showToast(validation.message, 'warning');

    try {
      setLoading(true);
      await decoupledSave('node', 'assignment', {
        start: new DayPilot.Date(args.newStart).toString('yyyy-MM-ddTHH:mm:ss'),
        end: new DayPilot.Date(args.newEnd).toString('yyyy-MM-ddTHH:mm:ss'),
        resource: args.newResource,
      }, args.e.data.id);
      await refreshAssignments();
    } catch (err) {
      showToast(`Erreur déplacement : ${err.message}`, 'error');
      args.preventDefault();
    } finally {
      setLoading(false);
    }
  }, [isAdmin, validateAction, showToast, refreshAssignments]);

  /* ── Resize event ── */
  const onEventResized = useCallback(async (args) => {
    const resizeWorkload = args.e.data._meta?.workload ?? 100;
    const validation = validateAction(args.e.data.resource, args.newStart, args.newEnd, resizeWorkload, args.e.data.id);
    if (validation.blocked) { showToast(validation.message, 'error'); args.preventDefault(); return; }
    if (validation.message) showToast(validation.message, 'warning');

    try {
      setLoading(true);
      await decoupledSave('node', 'assignment', {
        start: new DayPilot.Date(args.newStart).toString('yyyy-MM-ddTHH:mm:ss'),
        end: new DayPilot.Date(args.newEnd).toString('yyyy-MM-ddTHH:mm:ss'),
      }, args.e.data.id);
      await refreshAssignments();
    } catch (err) {
      showToast(`Erreur redimensionnement : ${err.message}`, 'error');
      args.preventDefault();
    } finally {
      setLoading(false);
    }
  }, [validateAction, showToast, refreshAssignments]);

  /* ── Navigation ── */
  const switchView = useCallback((view) => {
    setCustomDays(null);
    setCurrentView(view);
    setStartDate(getStartForView(view, DayPilot.Date.today()));
  }, []);

  const navigate = useCallback((dir) => {
    setCustomDays(null);
    const d = new DayPilot.Date(startDate);
    const next = currentView === 'week' ? d.addDays(dir * 7) : d.addMonths(dir);
    setStartDate(next);
  }, [startDate, currentView]);

  const goToday = useCallback(() => {
    setCustomDays(null);
    setStartDate(getStartForView(currentView, DayPilot.Date.today()));
  }, [currentView]);

  useEffect(() => {
    const s = new DayPilot.Date(startDate);
    const e = s.addDays(days - 1);
    setCustomStart(s.toString('yyyy-MM-dd'));
    setCustomEnd(e.toString('yyyy-MM-dd'));
  }, [startDate, days]);

  const handleApplyCustomRange = useCallback(() => {
    if (!customStart || !customEnd) return;
    const s = new DayPilot.Date(customStart);
    const e = new DayPilot.Date(customEnd);
    if (s.getTime() > e.getTime()) return;
    const diff = Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
    if (diff > 366) return;
    setCustomDays(diff);
    setStartDate(s);
    setIsDatePickerOpen(false);
  }, [customStart, customEnd]);

  /* ── Keyboard delete ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      if (e.target.closest('input, textarea, select')) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && lastClickedEvent.current) {
        e.preventDefault();
        callbacksRef.current.handleDelete(lastClickedEvent.current);
        lastClickedEvent.current = null;
      }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [callbacksRef]);

  /* ── Year view: progressive rendering ── */
  const dynamicProps = currentView === 'year'
    ? { dynamicEventRendering: 'Progressive', dynamicEventRenderingMarginX: 50 }
    : {};

  /* ── Render ── */
  return (
    <ActionDependant {...props}>
      <div {...attributes} ref={containerRef} tabIndex={-1} style={{ outline: 'none', width: '100%', minWidth: 0 }}>
        <SchedulerToolbar
          currentView={currentView}
          startDate={startDate}
          days={days}

          isDatePickerOpen={isDatePickerOpen}
          customStart={customStart}
          customEnd={customEnd}
          periodStats={periodStats}
          filterProject={filterProject}
          filterType={filterType}
          projectFilterOptions={projectFilterOptions}
          typeFilterOptions={typeFilterOptions}
          onNavigate={navigate}
          onGoToday={goToday}
          onSwitchView={switchView}
          onToggleDatePicker={() => setIsDatePickerOpen((prev) => !prev)}

          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
          onApplyCustomRange={handleApplyCustomRange}
          onFilterProjectChange={setFilterProject}
          onFilterTypeChange={setFilterType}
        />

        {/* Toast */}
        {toast && (
          <div
            className={`mb-3 px-4 py-2 rounded-md text-sm font-medium ${
              toast.type === 'error'
                ? 'bg-red-100 text-red-800 border border-red-200'
                : 'bg-amber-100 text-amber-800 border border-amber-200'
            }`}
          >
            {toast.msg}
            <button onClick={() => setToast(null)} className="ml-3 font-bold">&times;</button>
          </div>
        )}

        {/* Scheduler */}
        <div className="w-full min-w-0">
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-x-auto overflow-y-hidden relative">
            {loading && (
              <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-iris-600" />
              </div>
            )}
            <DayPilotScheduler
              key={`sched-${currentView}-${responsiveCellWidth}-${responsiveRowHeaderWidth}`}
              locale="fr-fr"
              scale={viewConfig.scale}
              cellDuration={viewConfig.cellDuration}
              timeHeaders={viewConfig.timeHeaders}
              startDate={startDate}
              days={days}
              cellWidth={responsiveCellWidth}
              eventHeight={38}
              eventBorderRadius={6}
              headerHeight={40}
              rowHeaderWidth={responsiveRowHeaderWidth}
              rowMinHeight={ROW_MIN_H}
              rowMarginTop={8}
              rowMarginBottom={8}
              durationBarVisible={false}
              treeEnabled={false}
              resources={schedulerResources}
              events={filteredEvents}
              separators={separators}
              crosshairType="Full"
              bubble={bubble}
              showToolTip={!bubble}
              contextMenu={eventMenu}
              onEventClick={onEventClick}
              onEventDoubleClick={onEventDoubleClick}
              onTimeRangeSelected={onTimeRangeSelected}
              onEventMoved={onEventMoved}
              onEventResized={onEventResized}
              eventMoveHandling="Update"
              eventResizeHandling="Update"
              timeRangeSelectedHandling="Enabled"
              onBeforeEventRender={onBeforeEventRender}
              onBeforeCellRender={onBeforeCellRender}
              onBeforeTimeHeaderRender={onBeforeTimeHeaderRender}
              onBeforeRowHeaderRender={onBeforeRowHeaderRender}
              eventMovingStartEndEnabled={true}
              eventResizingStartEndEnabled={true}
              controlRef={setSchedulerRef}
              {...dynamicProps}
            />
          </div>
        </div>

        {modalPortal}
      </div>
    </ActionDependant>
  );
};
